import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { store } from '../store.js';
import type { Locale, MessageKey } from '../i18n/messages.js';
import { iconStyles } from '@healthgeek/ui';
import '@healthgeek/ui';
import { EXTERNAL } from '@healthgeek/utils';

const NAV = [
  { href: '/', key: 'nav.home' as const },
  { href: '/story', key: 'nav.mission' as const },
  { href: '/programs', key: 'nav.programs' as const },
  { href: '/governance', key: 'nav.governance' as const },
  { href: '/contact', key: 'nav.contact' as const },
] as const;

const DOC_LABEL: Record<string, string> = {
  '/': 'HOME',
  '/story': 'OUR-STORY',
  '/mission': 'OUR-STORY',
  '/programs': 'PROGRAMS',
  '/governance': 'GOVERNANCE',
  '/contact': 'CONTACT',
  '/impact': 'IMPACT',
  '/where-we-work': 'WHERE',
  '/learning': 'LEARNING',
  '/get-involved': 'GET-INVOLVED',
  '/get-involved/donate': 'DONATE',
  '/community': 'COMMUNITY',
  '/partners': 'PARTNERS',
  '/news': 'NEWS',
  '/transparency': 'FINANCIALS',
  '/mou_robotsmali': 'MOU-ROBOTSMALI',
};

function docId(path: string) {
  if (DOC_LABEL[path]) return DOC_LABEL[path];
  if (path.startsWith('/programs/')) return 'PROGRAMS';
  if (path.startsWith('/legal/')) return 'LEGAL';
  if (path.startsWith('/get-involved')) return 'GET-INVOLVED';
  return 'PAGE';
}

function isCurrent(path: string, href: string) {
  if (href === '/') return path === '/';
  if (href === '/story') return path === '/story' || path === '/mission';
  return path === href || path.startsWith(`${href}/`);
}

@customElement('hg-site-header')
export class HgSiteHeader extends LitElement {
  static styles = [
    iconStyles,
    css`
      :host {
        display: block;
      }
      .doc-id {
        background: var(--hg-paper-2);
        border-bottom: 1px solid var(--hg-line);
        font-size: 11.5px;
        color: var(--hg-muted);
        letter-spacing: 0.5px;
        padding: 7px 0;
      }
      .doc-id .row {
        max-width: var(--hg-max);
        margin: 0 auto;
        padding: 0 var(--hg-gutter);
        display: flex;
        justify-content: space-between;
        gap: 12px;
      }
      header {
        border-bottom: 1px solid var(--hg-line);
        background: var(--hg-paper);
      }
      a {
        color: inherit;
        text-decoration: none;
      }
      .nav-inner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 22px var(--hg-gutter);
        max-width: var(--hg-max);
        margin: 0 auto;
        gap: 12px;
      }
      .brand {
        font-weight: 700;
        font-size: 16px;
        letter-spacing: 1px;
        text-decoration: none;
        color: var(--hg-ink);
        flex-shrink: 0;
      }
      .brand span {
        color: var(--hg-forest);
      }
      nav.links {
        display: none;
        gap: 22px;
        font-size: 13.5px;
        color: var(--hg-ink);
      }
      nav.links a {
        text-decoration: none;
        color: inherit;
        border-bottom: 1px solid transparent;
        padding-bottom: 2px;
        white-space: nowrap;
      }
      nav.links a:hover,
      nav.links a[aria-current='page'] {
        border-bottom-color: var(--hg-forest);
        color: var(--hg-forest);
      }
      .nav-right {
        display: flex;
        align-items: center;
        gap: 14px;
        font-size: 12.5px;
        flex-shrink: 0;
        color: var(--hg-ink);
      }
      .lang {
        display: none;
        gap: 6px;
        color: var(--hg-muted);
      }
      .lang button {
        border: 0;
        background: transparent;
        padding: 0;
        font: inherit;
        font-size: 12.5px;
        color: var(--hg-muted);
        cursor: pointer;
      }
      .lang button[aria-pressed='true'] {
        color: var(--hg-ink);
        font-weight: 700;
      }
      .donate-desk {
        display: none;
      }
      .icon-btn {
        width: 2.5rem;
        height: 2.5rem;
        border: 0;
        border-radius: 0;
        display: grid;
        place-items: center;
        background: transparent;
        color: inherit;
        cursor: pointer;
      }
      .menu {
        display: grid;
      }
      @media (min-width: 900px) {
        nav.links {
          display: flex;
        }
        .lang,
        .donate-desk {
          display: flex;
        }
        .menu {
          display: none;
        }
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }

  private unsubscribe?: () => void;

  private t(key: MessageKey) {
    return store.t(key);
  }

  private path() {
    return location.pathname;
  }

  render() {
    const path = this.path();
    return html`
      <div class="doc-id">
        <div class="row">
          <span>HGO / ${docId(path)} / v2</span>
          <span>${this.t('doc.org')}</span>
        </div>
      </div>
      <header>
        <div class="nav-inner">
          <a class="brand" href="/" aria-label="HealthGeek">${this.t('brand.health')}<span>${this.t('brand.geek')}</span></a>
          <nav class="links" aria-label="Primary">
            ${NAV.map(
              (item) => html`
                <a href=${item.href} aria-current=${isCurrent(path, item.href) ? 'page' : nothing}>
                  ${this.t(item.key)}
                </a>
              `,
            )}
          </nav>
          <div class="nav-right">
            <div class="lang" role="group" aria-label="Language">
              ${(['en', 'fr', 'bm'] as Locale[]).map(
                (loc, i) => html`
                  ${i > 0 ? html`<span aria-hidden="true">/</span>` : nothing}
                  <button aria-pressed=${store.locale === loc} @click=${() => store.setLocale(loc)}>
                    ${this.t(`lang.${loc}` as MessageKey)}
                  </button>
                `,
              )}
            </div>
            <span class="donate-desk">
              <hg-button href="/contact#donate" size="sm">${this.t('nav.donate')}</hg-button>
            </span>
            <button
              class="icon-btn menu"
              type="button"
              aria-label=${this.t('nav.menu')}
              @click=${() => store.setDrawer(true)}
            >
              <span class="material-symbols-outlined">menu</span>
            </button>
          </div>
        </div>
      </header>
    `;
  }
}

@customElement('hg-nav-drawer')
export class HgNavDrawer extends LitElement {
  static styles = [
    iconStyles,
    css`
      :host {
        display: contents;
      }
      .scrim {
        position: fixed;
        inset: 0;
        background: var(--md-sys-color-scrim);
        z-index: 50;
        opacity: 0;
        pointer-events: none;
        transition: opacity var(--md-sys-motion);
      }
      .scrim.open {
        opacity: 1;
        pointer-events: auto;
      }
      aside {
        position: fixed;
        top: 0;
        right: 0;
        height: 100dvh;
        width: min(22rem, 88vw);
        background: var(--hg-paper);
        z-index: 51;
        transform: translateX(100%);
        visibility: hidden;
        pointer-events: none;
        transition: transform var(--md-sys-motion), visibility var(--md-sys-motion);
        display: flex;
        flex-direction: column;
        padding: 0.6rem 0.75rem 1.5rem;
        border-left: 1px solid var(--hg-line);
      }
      aside.open {
        transform: none;
        visibility: visible;
        pointer-events: auto;
      }
      .top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.35rem 0.25rem 0.85rem;
      }
      .brand {
        font-weight: 700;
        letter-spacing: 1px;
      }
      .brand span {
        color: var(--hg-forest);
      }
      .icon-btn {
        width: 2.5rem;
        height: 2.5rem;
        border: 0;
        display: grid;
        place-items: center;
        background: transparent;
        color: inherit;
        cursor: pointer;
      }
      a {
        color: inherit;
        text-decoration: none;
      }
      a.item {
        display: flex;
        align-items: center;
        min-height: 2.75rem;
        padding: 0 0.35rem;
        text-decoration: none;
        font-size: 14px;
        border-bottom: 1px solid var(--hg-line);
        color: var(--hg-ink);
      }
      a.item[aria-current='page'] {
        color: var(--hg-forest);
      }
      .spacer {
        flex: 1;
      }
      .donate {
        padding: 1rem 0.15rem 0;
      }
      .langs {
        display: flex;
        gap: 12px;
        padding: 1rem 0.35rem 0;
        font-size: 12.5px;
        color: var(--hg-muted);
      }
      .langs button {
        border: 0;
        background: transparent;
        font: inherit;
        color: inherit;
        cursor: pointer;
        padding: 0;
      }
      .langs button[aria-pressed='true'] {
        color: var(--hg-ink);
        font-weight: 700;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }

  private unsubscribe?: () => void;

  private t(key: MessageKey) {
    return store.t(key);
  }

  private close = () => store.setDrawer(false);

  render() {
    const open = store.drawerOpen;
    const path = location.pathname;
    return html`
      <div class="scrim ${open ? 'open' : ''}" @click=${this.close}></div>
      <aside
        class=${open ? 'open' : ''}
        role="dialog"
        aria-modal="true"
        aria-hidden=${open ? 'false' : 'true'}
        aria-label=${this.t('nav.menu')}
      >
        <div class="top">
          <div class="brand">${this.t('brand.health')}<span>${this.t('brand.geek')}</span></div>
          <button class="icon-btn" type="button" aria-label=${this.t('nav.close')} @click=${this.close}>
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        ${NAV.map(
          (item) => html`
            <a
              class="item"
              href=${item.href}
              aria-current=${isCurrent(path, item.href) ? 'page' : nothing}
              @click=${this.close}
            >
              ${this.t(item.key)}
            </a>
          `,
        )}
        <a class="item" href="/learning" @click=${this.close}>${this.t('nav.learning')}</a>
        <a class="item" href="/transparency" @click=${this.close}>${this.t('footer.reports')}</a>
        <div class="langs" role="group" aria-label="Language">
          ${(['en', 'fr', 'bm'] as Locale[]).map(
            (loc) => html`
              <button aria-pressed=${store.locale === loc} @click=${() => store.setLocale(loc)}>
                ${this.t(`lang.${loc}` as MessageKey)}
              </button>
            `,
          )}
        </div>
        <div class="spacer"></div>
        <div class="donate">
          <hg-button href="/contact#donate" block @click=${this.close}>${this.t('nav.donate')}</hg-button>
        </div>
      </aside>
    `;
  }
}

@customElement('hg-bottom-nav')
export class HgBottomNav extends LitElement {
  static styles = [
    iconStyles,
    css`
      :host {
        display: none;
      }
      @media (max-width: 899px) {
        :host {
          display: block;
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 30;
        }
      }
      nav {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        background: var(--hg-paper);
        border-top: 1px solid var(--hg-line);
        padding: 0.35rem 0.25rem calc(0.35rem + env(safe-area-inset-bottom));
      }
      a,
      button {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.15rem;
        text-decoration: none;
        color: var(--hg-muted);
        font-size: 0.62rem;
        font-weight: 700;
        letter-spacing: 0.3px;
        padding: 0.25rem 0;
        border: 0;
        background: transparent;
        cursor: pointer;
        font-family: inherit;
      }
      a[aria-current='page'],
      a.active,
      button.active {
        color: var(--hg-forest);
      }
      .material-symbols-outlined {
        font-size: 1.25rem;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }

  private unsubscribe?: () => void;

  render() {
    const path = location.pathname;
    const items = [
      { href: '/', icon: 'home', key: 'nav.home' as const, match: (p: string) => p === '/' },
      { href: '/story', icon: 'menu_book', key: 'nav.mission' as const, match: (p: string) => p === '/story' || p === '/mission' },
      { href: '/programs', icon: 'grid_view', key: 'nav.programs' as const, match: (p: string) => p.startsWith('/programs') },
      { href: '/contact#donate', icon: 'volunteer_activism', key: 'nav.donate' as const, match: (p: string) => p.includes('donate') || p === '/contact' },
      { href: '#more', icon: 'menu', key: 'nav.more' as const, match: () => false },
    ];
    return html`
      <nav aria-label="Mobile">
        ${items.map((item) =>
          item.href === '#more'
            ? html`<button type="button" class=${store.drawerOpen ? 'active' : ''} @click=${this.openMore}>
                <span class="material-symbols-outlined">${item.icon}</span>
                ${store.t(item.key)}
              </button>`
            : html`<a href=${item.href} aria-current=${item.match(path) ? 'page' : nothing}>
                <span class="material-symbols-outlined">${item.icon}</span>
                ${store.t(item.key)}
              </a>`,
        )}
      </nav>
    `;
  }

  private openMore = (event: Event) => {
    event.preventDefault();
    store.setDrawer(true);
  };
}

@customElement('hg-site-footer')
export class HgSiteFooter extends LitElement {
  static styles = css`
    :host {
      display: block;
      background: var(--hg-paper);
      color: var(--hg-muted);
      padding: 44px 0 30px;
      font-size: 13px;
    }
    @media (max-width: 899px) {
      :host {
        margin-bottom: var(--hg-bottom-nav-h);
      }
    }
    .wrap-wide {
      max-width: var(--hg-max);
      margin: 0 auto;
      padding: 0 var(--hg-gutter);
    }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 30px;
      margin-bottom: 30px;
    }
    @media (min-width: 800px) {
      .grid {
        grid-template-columns: 1.4fr 1fr 1fr 1fr;
      }
    }
    .brand {
      font-weight: 700;
      font-size: 16px;
      letter-spacing: 1px;
      color: var(--hg-ink);
      margin-bottom: 12px;
    }
    .brand span {
      color: var(--hg-forest);
    }
    .brand-blurb {
      max-width: 36ch;
      color: var(--hg-charcoal);
      font-size: 13px;
      margin: 0;
    }
    h4 {
      font-size: 12px;
      color: var(--hg-ink);
      margin: 0 0 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    a {
      display: block;
      color: var(--hg-ink);
      text-decoration: none;
      margin-bottom: 8px;
    }
    a:hover {
      color: var(--hg-forest);
    }
    .bottom {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 10px;
      padding-top: 18px;
      border-top: 1px solid var(--hg-line);
    }
    .legal {
      display: flex;
      flex-wrap: wrap;
      gap: 0.85rem;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }

  private unsubscribe?: () => void;

  render() {
    const t = (key: MessageKey) => store.t(key);
    return html`
      <footer>
        <div class="wrap-wide">
          <div class="grid">
            <div>
              <div class="brand">${t('brand.health')}<span>${t('brand.geek')}</span></div>
              <p class="brand-blurb">${t('footer.blurb')}</p>
            </div>
            <div>
              <h4>${t('footer.org')}</h4>
              <a href="/story">${t('footer.mission')}</a>
              <a href="/governance">${t('nav.governance')}</a>
              <a href="/transparency">${t('footer.reports')}</a>
            </div>
            <div>
              <h4>${t('footer.programs')}</h4>
              <a href="/programs#mali">${t('programs.mali.tag')}</a>
              <a href="${EXTERNAL.juvo}" target="_blank" rel="noopener">${t('programs.juvo.tag')}</a>
              <a href=${EXTERNAL.dental} target="_blank" rel="noopener">${t('programs.di.tag')}</a>
            </div>
            <div>
              <h4>${t('footer.touch')}</h4>
              <a href=${EXTERNAL.email}>contact@healthgeek.org.ml</a>
              <a href="tel:+22372150015" target="_blank" rel="noopener">+223 72 15 00 15</a>
              <a href="/contact">${t('footer.volunteer')}</a>
            </div>
          </div>
          <div class="bottom">
            <span>${t('footer.copy')}</span>
            <div class="legal">
              <a href="/mou_robotsmali">RobotsMali MoU</a>
              <a href="/legal/privacy">${t('page.privacy.title')}</a>
              <a href="/legal/safeguarding">${t('page.safeguarding.title')}</a>
              <a href="/transparency">${t('page.transparency.title')}</a>
            </div>
          </div>
        </div>
      </footer>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hg-site-header': HgSiteHeader;
    'hg-nav-drawer': HgNavDrawer;
    'hg-bottom-nav': HgBottomNav;
    'hg-site-footer': HgSiteFooter;
  }
}
